import { type FC, useMemo } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ConfirmationModal from 'components/molecules/common/modals/ConfirmationModal';
import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import { TPO_KEEP_FLAGS_MODAL } from '../../../config/testIds';
import type { CalendarRow } from '../../../types/calendar';
import useKeepFlagsMutation from '../hooks/mutations/useKeepFlagsMutation';
import { KEEP_FLAG_BY_MENU_KEY, type KeepFlag } from '../hooks/ui/useCalendarRowActions';

const antIcon = <LoadingOutlined style={{ fontSize: 16 }} spin />;

const CONFIRM_BUTTON_PROPS = { type: 'primary', style: { borderRadius: 6 } } as const;

const MODAL_WIDTH = 520;

export interface PendingKeepFlag {
    record: CalendarRow;
    flag: KeepFlag;
}

export interface KeepFlagsModalProps {
    pending: PendingKeepFlag | null;
    onClose: () => void;
}

const KeepFlagsModal: FC<KeepFlagsModalProps> = ({ pending, onClose }) => {
    const { defaultRowActionItems, labels } = useTpoGenConfig();
    const keepFlagLabels = labels.keepFlags;
    const { mutate: setKeepFlags, isPending } = useKeepFlagsMutation({ onSuccess: onClose });

    // The action's name is only authored once, on the menu item, so both the menu
    // and this modal read it from there rather than keeping a second copy in sync.
    const actionLabel = useMemo(() => {
        if (!pending) return '';
        for (const section of defaultRowActionItems) {
            const match = section.find((item) => KEEP_FLAG_BY_MENU_KEY[item.key] === pending.flag);
            if (match) return match.label;
        }
        return '';
    }, [pending, defaultRowActionItems]);

    if (!pending) return null;

    const { record, flag } = pending;
    const isSet = flag === 'keep_dates' ? !!record.keep_dates : !!record.keep_promo;
    const question = isSet ? keepFlagLabels.removeQuestion : keepFlagLabels.applyQuestion;

    return (
        <ConfirmationModal
            data-testid={TPO_KEEP_FLAGS_MODAL}
            open
            onCancel={onClose}
            onSubmitClick={() =>
                setKeepFlags({
                    promoId: record.promo_id ?? '',
                    ...(flag === 'keep_dates' ? { keepDates: !record.keep_dates } : { keepPromo: !record.keep_promo }),
                })
            }
            okText={isPending ? <Spin indicator={antIcon} /> : keepFlagLabels.confirmYes}
            cancelText={labels.common.cancel}
            okButtonProps={CONFIRM_BUTTON_PROPS}
            loading={isPending}
            title={isSet ? `${keepFlagLabels.removePrefix}${actionLabel}` : actionLabel}
            description={
                <div className="bg-[#fafafa] rounded-lg p-2">
                    <p className="text-sm text-[#18181b]">{question.replace('{action}', actionLabel)}</p>
                </div>
            }
            width={MODAL_WIDTH}
        />
    );
};

KeepFlagsModal.displayName = 'KeepFlagsModal';
export default KeepFlagsModal;
